// Implements: F-RPT-006, F-RPT-009, F-UIX-018, F-UIX-010
/**
 * Owner 2026-10-19: the org admin reads the organization's latency and errors per model and step
 * on Consumo through `GET /reports/organizations/{org}/latency`, and exports that window as a CSV
 * through its export twin ("Exportar CSV"). The period is chosen as on Plataforma > Estado (owner
 * 2026-10-19, answer 8A): "Este mes" (the month so far, by default) or "Últimos 7 días"; the read,
 * the range line and the export all follow it.
 *
 * Mounted only for an organization target. The period is state of one organization: another one
 * starts again on "Este mes" (keyed by its id, checklist 14). The read is cached by organization
 * and window (`orgLatencyKey`), so an answer for the period left behind is never drawn for the
 * new one; the day the period ends on is handed in (checklist 16). The
 * table and the button are the admin module's (`parts`, wired by the composition root). Loading,
 * empty and error (with its retry) are the section's own, one level under its heading (F-UIX-010);
 * the export stays offered, since it asks the same window on its own.
 */

import { useQuery } from "@tanstack/react-query";
import { useId, useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { asApiError } from "../../../shared/api/asApiError";
import { orgLatencyKey } from "../../../shared/api/queryKeys";
import { useTranslation } from "../../../shared/i18n";
import { EmptyState } from "../../../shared/ui/molecules/EmptyState";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { TableSkeleton } from "../../../shared/ui/molecules/TableSkeleton";
import { Select } from "../../../shared/ui/atoms/Select";
import { lastDays, monthToDate } from "../../../shared/ui/reportWindows";
import type { LatencyReportView, ReportWindow } from "../../../types/reports";
import { ORG_EXPORT_NAMES, downloadOrgReport, fetchOrgLatency } from "../api/orgReportsApi";
import { OrgLatency } from "../components/OrgLatency";
import { calendarDayText } from "../model/myUsage";
import type { ReportParts } from "./reportParts";

export interface OrgLatencyContainerProps {
  readonly organizationId: string;
  /** The day the period ends on. */
  readonly today: Date;
  readonly parts: ReportParts;
}

/** The periods Estado offers, in its own vocabulary (`reports.latency.period.*`). */
type Period = "month" | "week";

const WEEK_DAYS = 7;

function windowOf(period: Period, today: Date): ReportWindow {
  return period === "month" ? monthToDate(today) : lastDays(today, WEEK_DAYS);
}

export function OrgLatencyContainer(props: OrgLatencyContainerProps): ReactElement {
  return <OrganizationLatency key={props.organizationId} {...props} />;
}

function OrganizationLatency({ organizationId, today, parts }: OrgLatencyContainerProps): ReactElement {
  const { t } = useTranslation();
  const periodId = useId();
  const [period, setPeriod] = useState<Period>("month");
  const { LatencyTable, ExportCsv } = parts;
  const window = windowOf(period, today);
  const latency = useQuery<LatencyReportView, ApiError>({
    queryKey: orgLatencyKey(organizationId, window.from, window.to),
    queryFn: ({ signal }) => fetchOrgLatency(organizationId, window, signal),
  });

  const body = ((): ReactElement => {
    if (latency.isError) {
      return (
        <ErrorState
          headingLevel={4}
          error={asApiError(latency.error)}
          onRetry={() => {
            void latency.refetch();
          }}
        />
      );
    }
    if (latency.data === undefined) return <TableSkeleton />;
    if (latency.data.rows.length === 0) {
      return <EmptyState icon="chart" headingLevel={4} title={t("reports.latency.empty.title")} body={t("reports.latency.empty.body")} />;
    }
    return <LatencyTable rows={latency.data.rows} />;
  })();

  return (
    <OrgLatency
      range={t("backoffice.myUsage.period.range", { from: calendarDayText(window.from), to: calendarDayText(window.to) })}
      period={
        <Select
          id={periodId}
          label={t("reports.latency.period")}
          value={period}
          options={[
            { value: "month", label: t("reports.latency.period.month") },
            { value: "week", label: t("reports.latency.period.week") },
          ]}
          onChange={(value) => {
            setPeriod(value === "week" ? "week" : "month");
          }}
        />
      }
      actions={
        <ExportCsv
          download={() => downloadOrgReport(organizationId, "latency", window)}
          fallbackName={ORG_EXPORT_NAMES.latency}
        />
      }
    >
      {body}
    </OrgLatency>
  );
}
